/**
 * Size Guide popup
 *
 * Opens/closes `[data-size-popup]` dialogs from `[data-size-popup-open]` triggers.
 * Uses document-level event delegation so it keeps working when product sections
 * are re-rendered (e.g. Shopify Theme Editor section reloads) without rebinding,
 * and supports any number of popup instances on the page.
 */
(() => {
  'use strict';

  // Guard against the script being evaluated more than once.
  if (window.__sizeGuidePopupInit) return;
  window.__sizeGuidePopupInit = true;

  const ACTIVE_CLASS = 'is-active';
  const SCROLL_LOCK_CLASS = 'size-guide-modal-open';
  const TRANSITION_FALLBACK_MS = 400;
  const FOCUSABLE_SELECTOR = 'summary, a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';

  /** @type {Element|null} Currently open popup. */
  let activeModal = null;
  /** @type {Element|null} Trigger that opened the current popup. */
  let activeTrigger = null;
  /** @type {WeakMap<Element, number>} Pending hide timers, per popup. */
  const hideTimers = new WeakMap();

  /**
   * Returns the visible, focusable elements inside a popup.
   * Tolerates optional content (heading/description/images) being absent.
   * @param {Element} modal - Popup element.
   * @returns {Element[]}
   */
  const getFocusable = (modal) => Array.from(modal.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((el) => el.offsetParent !== null || el === document.activeElement);

  /**
   * Handles Escape and Tab (focus trap) while a popup is open.
   * @param {KeyboardEvent} evt - Event object.
   */
  const handleKeydown = (evt) => {
    if (!activeModal) return;

    if (evt.key === 'Escape') {
      closeModal();
      return;
    }

    if (evt.key !== 'Tab') return;

    const focusable = getFocusable(activeModal);
    if (focusable.length === 0) {
      evt.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const outside = !activeModal.contains(document.activeElement);

    if (evt.shiftKey && (document.activeElement === first || outside)) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && (document.activeElement === last || outside)) {
      evt.preventDefault();
      first.focus();
    }
  };

  /**
   * Opens a popup.
   * @param {Element} modal - Popup element.
   * @param {Element|null} trigger - Trigger that opened it (focus is returned here on close).
   */
  const openModal = (modal, trigger) => {
    if (activeModal === modal) return;
    if (activeModal) closeModal(false);

    window.clearTimeout(hideTimers.get(modal));
    activeModal = modal;
    activeTrigger = trigger || null;

    modal.removeAttribute('hidden');
    void modal.offsetWidth; // Force reflow so the opening transition runs.
    modal.classList.add(ACTIVE_CLASS);

    document.documentElement.classList.add(SCROLL_LOCK_CLASS);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    const closeBtn = modal.querySelector('[data-size-popup-close]');
    const focusTarget = closeBtn || getFocusable(modal)[0];
    if (focusTarget) focusTarget.focus();

    document.addEventListener('keydown', handleKeydown);
  };

  /**
   * Closes the open popup.
   * @param {boolean} [restoreFocus=true] - Return focus to the opening trigger.
   */
  const closeModal = (restoreFocus = true) => {
    if (!activeModal) return;

    const modal = activeModal;
    const trigger = activeTrigger;
    activeModal = null;
    activeTrigger = null;

    modal.classList.remove(ACTIVE_CLASS);
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    document.removeEventListener('keydown', handleKeydown);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    // Re-apply [hidden] after the fade-out transition (with a fallback in case
    // transitionend never fires, e.g. reduced motion).
    const hide = () => {
      window.clearTimeout(hideTimers.get(modal));
      if (modal !== activeModal) modal.setAttribute('hidden', '');
    };
    modal.addEventListener('transitionend', hide, { once: true });
    hideTimers.set(modal, window.setTimeout(hide, TRANSITION_FALLBACK_MS));

    if (restoreFocus && trigger) trigger.focus();
  };

  document.addEventListener('click', (evt) => {
    if (!(evt.target instanceof Element)) return;

    const opener = evt.target.closest('[data-size-popup-open]');
    if (opener) {
      const modal = document.getElementById(opener.getAttribute('data-size-popup-open'));
      if (modal) {
        evt.preventDefault();
        openModal(modal, opener);
      }
      return;
    }

    if (!activeModal) return;

    if (evt.target.closest('[data-size-popup-close]') && activeModal.contains(evt.target)) {
      closeModal();
      return;
    }

    // Click on the backdrop, outside the dialog itself.
    if (evt.target === activeModal || evt.target.closest('[data-size-popup-overlay]')) {
      closeModal();
    }
  });

  // Theme Editor: if the section containing the open popup is unloaded,
  // unlock page scrolling (the popup markup is removed with the section).
  document.addEventListener('shopify:section:unload', (evt) => {
    if (activeModal && evt.target.contains(activeModal)) closeModal(false);
  });
})();
